import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { generateQuotePdf } from "../lib/pdf";
import { Download, Eye, Plus, FileText } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SalesDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const pipeline = quotes.filter((q) => ["sent", "accepted"].includes(q.status)).reduce((s, q) => s + (q.one_time_total || 0), 0);
  const won = quotes.filter((q) => q.status === "accepted").reduce((s, q) => s + (q.one_time_total || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="sales-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Salesperson</span>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">My Quotes</h1>
          <p className="mt-1 font-mono text-xs text-zinc-500">Welcome back, {user?.name || user?.email}.</p>
        </div>
        <Button onClick={() => navigate("/build")} data-testid="sales-new-quote-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
          <Plus className="h-4 w-4 mr-1" /> New Quote
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-100 p-6" data-testid="sales-stat-total">
          <FileText className="h-5 w-5 text-blue-600" />
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">My Quotes</p>
          <p className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tighter">{loading ? "—" : quotes.length}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-6" data-testid="sales-stat-pipeline">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Pipeline</p>
          <p className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tighter">{fmt(pipeline)}</p>
        </div>
        <div className="bg-red-50 border border-red-100 p-6" data-testid="sales-stat-won">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">Won</p>
          <p className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tighter">{fmt(won)}</p>
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="text-left px-4 py-3">Quote</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">One-time</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`sales-quote-row-${q.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/quotes/${q.id}`)} className="font-medium hover:text-primary">#{q.id.slice(-8).toUpperCase()}</button>
                    <p className="text-xs text-zinc-400 font-mono">{new Date(q.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{q.client_name}</p>
                    <p className="text-xs text-zinc-500">{q.client_email}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(q.one_time_total)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" data-testid={`sales-view-${q.id}`} onClick={() => navigate(`/quotes/${q.id}`)} className="rounded-none h-8 px-2" title="View"><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" data-testid={`sales-download-${q.id}`} onClick={() => generateQuotePdf(q)} className="rounded-none h-8 border-zinc-300 px-2" title="Download PDF"><Download className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {quotes.length === 0 && <p className="p-10 text-center text-zinc-500" data-testid="sales-empty">No quotes assigned to you yet. Build one to get started.</p>}
        </div>
      )}
    </div>
  );
}
